import React, { useState, useRef, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { chatbotUrl } from '../../util/config';

const MODE_COLORS = {
    rag_only:       { bg: '#f0fdf4', color: '#16a34a', label: 'RAG' },
    rag:            { bg: '#f0fdf4', color: '#16a34a', label: 'RAG' },
    strict:         { bg: '#eff6ff', color: '#1565c0', label: 'Flow' },
    flow:           { bg: '#eff6ff', color: '#1565c0', label: 'Flow' },
    hybrid:         { bg: '#fffbeb', color: '#d97706', label: 'Hybrid' },
    handoff:        { bg: '#fef2f2', color: '#dc2626', label: 'Agent' },
    semantic_cache: { bg: '#f5f3ff', color: '#7c3aed', label: 'Cache' },
    fallback:       { bg: '#f9fafb', color: '#6b7280', label: 'Fallback' },
    error:          { bg: '#fef2f2', color: '#dc2626', label: 'Error' },
};

const QUICK_MSGS = [
    { label: 'Hello 👋',             text: 'Hello' },
    { label: 'Block debit card 💳',   text: 'How do I block my debit card?' },
    { label: 'Savings account 🏦',    text: 'How do I open a savings account?' },
    { label: 'Talk to agent 🧑‍💼',     text: 'I want to talk to an agent' },
    { label: 'Agent (Hindi) 🇮🇳',     text: 'agent se baat karni hai' },
    { label: 'Weather ☀️',            text: 'What is the weather today?' },
];

function getInitials(name) {
    if (!name) return 'U';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function formatTime(ts) {
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export default function TestChat() {
    const [messages,    setMessages]    = useState([]);
    const [inputText,   setInputText]   = useState('');
    const [sending,     setSending]     = useState(false);
    const [phoneNo,     setPhoneNo]     = useState('919876543210');
    const [phoneId,     setPhoneId]     = useState('518623944663807');
    const [userName,    setUserName]    = useState('Demo User');
    const [showConfig,  setShowConfig]  = useState(false);
    const [sessionInfo, setSessionInfo] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef       = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const addMessage = (role, text, meta = {}) => {
        const msg = { id: Date.now() + Math.random(), role, text, time: Date.now(), ...meta };
        setMessages(prev => [...prev, msg]);
        return msg;
    };

    const addSystem = (text) => addMessage('system', text);
    const addThinking = () => {
        const id = Date.now() + Math.random();
        setMessages(prev => [...prev, { id, role: 'thinking', text: '...', time: Date.now() }]);
        return id;
    };
    const removeThinking = (id) => setMessages(prev => prev.filter(m => m.id !== id));

    const sendMessage = async (text) => {
        const msg = (text || inputText).trim();
        if (!msg || sending) return;
        setInputText('');
        setSending(true);

        addMessage('user', msg);
        const thinkId = addThinking();

        const token = localStorage.getItem('token');

        try {
            const res  = await fetch(`${chatbotUrl}/test/simulate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    from:            phoneNo,
                    text:            msg,
                    phone_number_id: phoneId,
                }),
            });

            const data = await res.json();
            removeThinking(thinkId);

            if (data.success && data.data) {
                addMessage('bot', data.data.response, {
                    mode:   data.data.mode,
                    source: data.data.source,
                });
                setSessionInfo({ mode: data.data.mode, source: data.data.source });
            } else {
                addSystem(`❌ Error: ${data.error || 'No response'}`);
            }
        } catch (err) {
            removeThinking(thinkId);
            addSystem(`❌ Error: ${err.message}`);
        }

        setSending(false);
        inputRef.current?.focus();
    };

    // Poll for agent messages when in handoff mode
    useEffect(() => {
        if (!sessionInfo || sessionInfo.mode !== 'handoff') return;
        const token = localStorage.getItem('token');
        const interval = setInterval(async () => {
            try {
                const res  = await fetch(`${chatbotUrl}/test/agent-messages?phone=${phoneNo}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (data.success && data.data?.length > 0) {
                    data.data.forEach(msg => {
                        setMessages(prev => {
                            if (prev.find(m => m.agentMsgId === msg.id)) return prev;
                            return [...prev, { id: Date.now() + Math.random(), agentMsgId: msg.id, role: 'bot', text: msg.message, time: new Date(msg.timestamp).getTime(), mode: 'handoff', source: 'agent' }];
                        });
                    });
                }
            } catch (e) { /* ignored */ }
        }, 2000);
        return () => clearInterval(interval);
    }, [sessionInfo, phoneNo]);

    const clearChat = async () => {
        setMessages([]);
        setSessionInfo(null);
        // Reset session in backend
        try {
            const token = localStorage.getItem('token');
            await fetch(`${chatbotUrl}/test/reset-session?phone=${phoneNo}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Session reset', { autoClose: 1500 });
        } catch (e) { /* ignored */ }
    };

    const modeMeta = (mode, source) => {
        const key = source === 'semantic_cache' ? 'semantic_cache'
            : source === 'fallback' ? 'fallback'
            : mode;
        return MODE_COLORS[key] || MODE_COLORS.rag_only;
    };

    return (
        <WrapperComponent>
            <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)', fontFamily: "'Segoe UI', system-ui, sans-serif", gap: 0 }}>
                {/* Page header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
                    <div>
                        <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Test Chat</h5>
                        <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>Simulate WhatsApp conversations with your AI agent</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setShowConfig(p => !p)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: showConfig ? '#eff6ff' : '#fff', border: `1px solid ${showConfig ? '#1565c0' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: showConfig ? '#1565c0' : '#374151' }}>
                            <i className="bi bi-gear"></i> Config
                        </button>
                        <button onClick={clearChat}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                            <i className="bi bi-arrow-clockwise"></i> Reset
                        </button>
                    </div>
                </div>

                {/* Config panel */}
                {showConfig && (
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, flexShrink: 0 }}>
                        <div>
                            <label style={lbl}>WhatsApp Number (from)</label>
                            <input value={phoneNo} onChange={e => setPhoneNo(e.target.value)} style={inp} placeholder="919876543210"/>
                        </div>
                        <div>
                            <label style={lbl}>Phone Number ID</label>
                            <input value={phoneId} onChange={e => setPhoneId(e.target.value)} style={inp} placeholder="518623944663807"/>
                        </div>
                        <div>
                            <label style={lbl}>Display Name</label>
                            <input value={userName} onChange={e => setUserName(e.target.value)} style={inp} placeholder="Demo User"/>
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', flex: 1, gap: 16, overflow: 'hidden' }}>
                    {/* Chat window */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        {/* WA Header */}
                        <div style={{ background: '#075e54', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                🤖
                            </div>
                            <div style={{ flex: 1 }}>
                                <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>WhatsApp AI Agent</p>
                                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', margin: 0 }}>
                                    {sending ? 'typing...' : 'online'}
                                </p>
                            </div>
                            {sessionInfo && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ ...modeMeta(sessionInfo.mode, sessionInfo.source), fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10 }}>
                                        {modeMeta(sessionInfo.mode, sessionInfo.source).label}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Messages */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#ece5dd', display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {messages.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                                    <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
                                    <p style={{ fontWeight: 600, color: '#374151', fontSize: 14, margin: 0 }}>Start a conversation</p>
                                    <p style={{ fontSize: 12, marginTop: 4 }}>Send a message or use quick messages on the right</p>
                                </div>
                            )}

                            {messages.map(msg => (
                                <div key={msg.id} style={{ display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8 }}>
                                    {msg.role === 'system' ? (
                                        <div style={{ width: '100%', textAlign: 'center' }}>
                                            <span style={{ fontSize: 11, color: '#6b7280', background: 'rgba(0,0,0,0.06)', padding: '3px 12px', borderRadius: 20 }}>{msg.text}</span>
                                        </div>
                                    ) : msg.role === 'thinking' ? (
                                        <div style={{ background: '#fff', padding: '10px 14px', borderRadius: '4px 16px 16px 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                {[0,1,2].map(i => (
                                                    <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', animation: `bounce 1.2s ${i*0.2}s infinite` }}/>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', gap: 3, alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                            <div style={{
                                                padding: '10px 14px',
                                                borderRadius: msg.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                                                background: msg.role === 'user' ? '#dcf8c6' : '#fff',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                fontSize: 13,
                                                color: '#111827',
                                                lineHeight: 1.5,
                                                whiteSpace: 'pre-wrap',
                                            }}>
                                                {msg.text}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 4, paddingRight: 4 }}>
                                                <span style={{ fontSize: 10, color: '#9ca3af' }}>{formatTime(msg.time)}</span>
                                                {msg.role === 'bot' && msg.mode && (
                                                    <span style={{ ...modeMeta(msg.mode, msg.source), fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8 }}>
                                                        {modeMeta(msg.mode, msg.source).label}
                                                        {msg.source === 'semantic_cache' && ' ⚡'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            <div ref={messagesEndRef}/>
                        </div>

                        {/* Input */}
                        <div style={{ background: '#f0f0f0', padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
                            <div style={{ flex: 1, background: '#fff', borderRadius: 24, padding: '8px 16px', display: 'flex', alignItems: 'center' }}>
                                <textarea
                                    ref={inputRef}
                                    value={inputText}
                                    onChange={e => setInputText(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                                    placeholder="Type a message..."
                                    rows={1}
                                    style={{ flex: 1, border: 'none', outline: 'none', resize: 'none', fontSize: 14, background: 'transparent', fontFamily: 'inherit', maxHeight: 88, lineHeight: 1.4 }}
                                />
                            </div>
                            <button onClick={() => sendMessage()} disabled={!inputText.trim() || sending}
                                style={{ width: 44, height: 44, borderRadius: '50%', background: !inputText.trim() || sending ? '#d1d5db' : '#25d366', border: 'none', cursor: !inputText.trim() || sending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className="bi bi-send-fill" style={{ fontSize: 16, color: '#fff' }}></i>
                            </button>
                        </div>
                    </div>

                    {/* Right panel — quick messages + legend */}
                    <div style={{ width: 240, display: 'flex', flexDirection: 'column', gap: 12, flexShrink: 0 }}>
                        {/* Quick messages */}
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                            <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Quick Messages</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {QUICK_MSGS.map(q => (
                                    <button key={q.text} onClick={() => sendMessage(q.text)} disabled={sending}
                                        style={{ padding: '8px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, cursor: sending ? 'not-allowed' : 'pointer', fontSize: 12, color: '#374151', textAlign: 'left', transition: 'all 0.15s' }}
                                        onMouseEnter={e => { if (!sending) { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.color = '#1565c0'; }}}
                                        onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}>
                                        {q.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Mode legend */}
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                            <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Response Modes</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[
                                    { mode: 'rag_only',  label: 'RAG',      desc: 'Knowledge base answer' },
                                    { mode: 'flow',      label: 'Flow',     desc: 'Strict flow execution' },
                                    { mode: 'hybrid',    label: 'Hybrid',   desc: 'Flow + RAG combined' },
                                    { mode: 'semantic_cache', label: 'Cache ⚡', desc: 'Instant cached answer' },
                                    { mode: 'handoff',   label: 'Agent',    desc: 'Human agent assigned' },
                                    { mode: 'fallback',  label: 'Fallback', desc: 'No answer found' },
                                ].map(item => (
                                    <div key={item.mode} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ ...MODE_COLORS[item.mode], fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8, flexShrink: 0 }}>{item.label}</span>
                                        <span style={{ fontSize: 11, color: '#6b7280' }}>{item.desc}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Session info */}
                        {sessionInfo && (
                            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                                <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Last Response</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#6b7280' }}>Mode</span>
                                        <span style={{ fontWeight: 600, color: '#111827' }}>{sessionInfo.mode}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#6b7280' }}>Source</span>
                                        <span style={{ fontWeight: 600, color: '#111827' }}>{sessionInfo.source}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <ToastContainer/>
            </div>

            <style>{`
                @keyframes bounce {
                    0%, 60%, 100% { transform: translateY(0); }
                    30% { transform: translateY(-4px); }
                }
            `}</style>
        </WrapperComponent>
    );
}

const lbl = { fontSize: 11, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 12, color: '#111827', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
